import { useCallback, useEffect, useMemo } from "react";

import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useAlertsParams } from "../../hooks/useAlertsParams";

import type { AlertTabId } from "./config";
import type { AlertViewMode } from "./viewModeConfig";
import { DEFAULT_VIEW_MODE, TABS_WITHOUT_METRICS, VIEW_MODES } from "./viewModeConfig";

const resolveViewMode = (viewMode: AlertViewMode, tab: AlertTabId | undefined): AlertViewMode => {
    if (viewMode === "metrics" && tab !== undefined && TABS_WITHOUT_METRICS.includes(tab)) {
        return DEFAULT_VIEW_MODE;
    }

    return viewMode;
};

const getValidViewMode = (urlViewMode: AlertViewMode | undefined, tab: AlertTabId | undefined): AlertViewMode => {
    const baseMode = urlViewMode && VIEW_MODES.some((m) => m.id === urlViewMode) ? urlViewMode : DEFAULT_VIEW_MODE;

    return resolveViewMode(baseMode, tab);
};

export const useViewModeFromUrl = (currentTab: AlertTabId) => {
    const { viewMode: urlViewMode } = useAlertsParams();

    const currentViewMode = useMemo(() => getValidViewMode(urlViewMode, currentTab), [urlViewMode, currentTab]);

    const setViewMode = useCallback((mode: AlertViewMode) => {
        proNavigator.updateQueryParams({ viewMode: mode }, { reload: false });
    }, []);

    const replaceViewMode = useCallback((mode: AlertViewMode) => {
        proNavigator.updateQueryParams({ viewMode: mode }, { reload: false, location: "replace" });
    }, []);

    useEffect(() => {
        const resolved = resolveViewMode(currentViewMode, currentTab);
        if (resolved !== urlViewMode && urlViewMode !== undefined) {
            replaceViewMode(resolved);
        }
    }, [currentTab, currentViewMode, urlViewMode, replaceViewMode]);

    const handleViewModeChange = useCallback(
        (value: string) => {
            const mode = VIEW_MODES.find((m) => m.id === value);
            if (mode) {
                setViewMode(mode.id);
            }
        },
        [setViewMode],
    );

    return { currentViewMode, handleViewModeChange };
};
